import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  useToast,
  VStack,
  useColorModeValue,
  Flex,
  Image,
  Center,
  Heading,
  Link,
  HStack,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import bgImage from '../assets/images/HansCard_L.png';
import { useTranslation } from 'react-i18next';
import LogoWithBackground from '../components/LogoWithBackground';

export default function ForgotPassword() {
  // Add translation hook
  const { t } = useTranslation();

  // State hooks
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Auth hooks
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Theme values
  const formBgColor = useColorModeValue(
    'rgba(255, 255, 255, 0.85)',
    `rgba(34, 35, 57, 0.85)`
  );
  const borderColor = useColorModeValue('gray.200', 'rgba(255, 255, 255, 0.1)');
  const shadowColor = useColorModeValue(
    '0 4px 20px rgba(0, 0, 0, 0.1)',
    '0 4px 20px rgba(0, 0, 0, 0.3)'
  );
  const backdropBgColor = useColorModeValue(
    'rgba(255, 255, 255, 0.4)',
    'rgba(0, 0, 0, 0.4)'
  );
  const logoFilter = useColorModeValue('none', 'brightness(1.2)');
  const inputBgColor = useColorModeValue('white', 'rgba(45, 55, 72, 0.5)');
  const inputFocusBgColor = useColorModeValue('white', 'rgba(45, 55, 72, 0.8)');
  const hoverBoxShadow = useColorModeValue(
    '0 8px 30px rgba(0, 0, 0, 0.12)',
    '0 8px 30px rgba(0, 0, 0, 0.4)'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await resetPassword(email);
      setIsSuccess(true);
      toast({
        title: t('auth.resetLinkSent'),
        description: t('auth.checkEmailForInstructions'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('auth.authError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minHeight="100vh"
      width="100%"
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      backgroundImage={`url(${bgImage})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundRepeat="no-repeat"
      overflow="hidden"
    >
      <Flex
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        alignItems="center"
        justifyContent="center"
        backdropFilter="blur(5px)"
        bg={backdropBgColor}
      >
        <Container
          maxW="md"
          py={10}
          px={4}
          position="relative"
          zIndex={1}
          mx="auto"
          centerContent
        >
          <Center mb={8} w="full">
            <LogoWithBackground
              width={{ base: "200px", md: "250px" }}
              objectFit="contain"
              transition="filter 0.2s ease"
            />
          </Center>

          <Box
            p={{ base: 6, md: 8 }}
            width="100%"
            bg={formBgColor}
            boxShadow={shadowColor}
            borderRadius="xl"
            border="1px"
            borderColor={borderColor}
            backdropFilter="blur(10px)"
            transition="all 0.3s ease"
            _hover={{
              boxShadow: hoverBoxShadow,
              transform: 'translateY(-2px)'
            }}
          >
            <VStack spacing={6}>
              <Heading as="h1" size="lg" textAlign="center">
                {t('auth.resetPassword')}
              </Heading>

              {isSuccess ? (
                <VStack spacing={4}>
                  <Text textAlign="center">
                    {t('auth.resetEmailSentMessage')}
                  </Text>
                  <Button
                    colorScheme="brand"
                    w="full"
                    onClick={() => navigate('/login')}
                  >
                    {t('auth.backToLogin')}
                  </Button>
                </VStack>
              ) : (
                <VStack as="form" onSubmit={handleSubmit} spacing={4} w="full">
                  <FormControl id="email" isRequired>
                      <FormLabel>{t('auth.email')}</FormLabel>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      bg={inputBgColor}
                      _focus={{
                        bg: inputFocusBgColor,
                        borderColor: 'brand.500'
                      }}
                    />
                  </FormControl>

                  <Stack spacing={4} w="full">
                    <Button
                      colorScheme="brand"
                      type="submit"
                      isLoading={isLoading}
                      w="full"
                    >
                        {t('auth.resetPassword')}
                    </Button>
                    <Button
                      variant="outline"
                      w="full"
                      onClick={() => navigate('/login')}
                    >
                        {t('auth.backToLogin')}
                    </Button>
                  </Stack>
                </VStack>
              )}
            </VStack>
          </Box>
        </Container>
      </Flex>
    </Box>
  );
}